import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, LogOut, AlertCircle } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";

export const InstagramIntegrationCard = ({ authAxios, restaurant }) => {
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchConnectionStatus();
  }, []);

  const fetchConnectionStatus = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/social-agent/instagram/connection/status");
      setConnection(res.data);
    } catch (e) {
      console.error("[INSTAGRAM] Failed to fetch connection status:", e);
      setConnection({ connected: false });
    }
    setLoading(false);
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await authAxios.post("/social-agent/instagram/oauth/start");
      window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error starting OAuth");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setConnecting(true);
    try {
      await authAxios.post("/social-agent/instagram/disconnect");
      toast.success("Instagram desconectado");
      await fetchConnectionStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error disconnecting");
    }
    setConnecting(false);
  };

  const getStatusBadge = () => {
    if (loading) return <Badge variant="secondary">Carregando...</Badge>;
    if (!connection?.connected) return <Badge variant="outline">Desconectado</Badge>;
    if (connection?.status === "reconnect_required") return <Badge variant="destructive">Reconexão necessária</Badge>;
    return <Badge variant="default" className="bg-green-600">Conectado</Badge>;
  };

  const getStatusIcon = () => {
    if (!connection?.connected || connection?.status === "reconnect_required") {
      return <AlertCircle className="w-4 h-4 text-orange-500" />;
    }
    return null;
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <CardTitle className="uppercase flex items-center gap-2">
          Instagram — Karol
          {getStatusIcon()}
        </CardTitle>
        <CardDescription>Conectar conta para comentários e respostas automáticas (fase de testes).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Status da conexão</p>
            {getStatusBadge()}
          </div>
          {connection?.connected && connection?.token_expires_at && (
            <div className="text-xs text-muted-foreground">
              Expira: {new Date(connection.token_expires_at).toLocaleDateString()}
            </div>
          )}
        </div>

        {connection?.connected && (
          <div className="space-y-3 p-3 bg-muted rounded-lg text-sm">
            <p><strong>Conta:</strong> {connection.ig_account_id}</p>
            <p><strong>Status:</strong> {connection.status === "connected" ? "Ativo" : "Aguardando ação"}</p>
            {connection.linkage_status && (
              <p><strong>Verificação:</strong> {connection.linkage_status === "verified" ? "✓ Confirmada" : "⚠ Pendente"}</p>
            )}
          </div>
        )}

        <div className="flex gap-2">
          {!connection?.connected ? (
            <Button
              className="glow-orange"
              onClick={handleConnect}
              disabled={connecting || loading}
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Conectar Instagram
            </Button>
          ) : (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDisconnect}
              disabled={connecting}
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogOut className="w-4 h-4 mr-2" />}
              Desconectar
            </Button>
          )}
        </div>

        {connection?.status === "reconnect_required" && (
          <div className="text-xs text-orange-600 p-2 bg-orange-50 rounded border border-orange-200">
            A conexão expirou ou não foi renovada. Por favor, reconecte sua conta.
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Você também pode remover a autorização diretamente no Instagram: Configurações → Aplicativos e sites → Desconectar.
        </p>
      </CardContent>
    </Card>
  );
};
